import { Controller } from "react-hook-form";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import type { Role } from "@/app/_services/RbacService";

interface InviteUserFormProps {
  control: any;
  errors: any;
  roles: Role[];
  isEditing: boolean;
}

export function InviteUserForm({
  control,
  errors,
  roles,
  isEditing,
}: InviteUserFormProps) {
  return (
    <div className="grid gap-4">
      <div className="grid gap-3">
        <Label htmlFor="email">Email</Label>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              id="email"
              type="email"
              {...field}
              value={field.value ?? ""}
              placeholder="name@comedyuo.com"
              autoComplete="off"
              // Email is the identity WorkOS matches on, so it is fixed after invite.
              disabled={isEditing}
            />
          )}
        />
        {errors.email && (
          <span className="text-[12px] text-red-600">{errors.email?.message}</span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-3">
          <Label htmlFor="first_name">First name</Label>
          <Controller
            name="first_name"
            control={control}
            render={({ field }) => (
              <Input
                id="first_name"
                {...field}
                value={field.value ?? ""}
                disabled={isEditing}
              />
            )}
          />
        </div>
        <div className="grid gap-3">
          <Label htmlFor="last_name">Last name</Label>
          <Controller
            name="last_name"
            control={control}
            render={({ field }) => (
              <Input
                id="last_name"
                {...field}
                value={field.value ?? ""}
                disabled={isEditing}
              />
            )}
          />
        </div>
      </div>

      <div className="grid gap-3">
        <Label>Roles</Label>
        <Controller
          name="role_ids"
          control={control}
          render={({ field }) => {
            const selected: number[] = field.value ?? [];
            const toggle = (id: number) =>
              field.onChange(
                selected.includes(id)
                  ? selected.filter((value) => value !== id)
                  : [...selected, id]
              );
            return (
              <div className="grid gap-2 rounded-md border border-border p-3">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className="flex cursor-pointer items-start gap-2.5"
                  >
                    <Checkbox
                      checked={selected.includes(role.id)}
                      onCheckedChange={() => toggle(role.id)}
                      className="mt-0.5"
                    />
                    <span className="grid gap-0.5">
                      <span className="text-[12.5px] font-medium leading-none">
                        {role.name}
                      </span>
                      {role.description && (
                        <span className="text-[12px] text-muted-foreground">
                          {role.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            );
          }}
        />
        {errors.role_ids && (
          <span className="text-[12px] text-red-600">
            {errors.role_ids?.message}
          </span>
        )}
      </div>
    </div>
  );
}
